import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { z } from "zod";

export const COMPOSIO_SERVER_NAME = "composio" as const;

export const COMPOSIO_TOOLKITS = [
  "slack",
  "linear",
  "jira",
  "gmail",
  "googlecalendar",
  "googledrive",
  "googledocs",
  "googlesheets",
  "googleslides",
  "googlemeet",
  "googletasks",
  "googlephotos",
] as const;

const toolkitSchema = z
  .enum(COMPOSIO_TOOLKITS)
  .describe(`Composio toolkit (app) slug: ${COMPOSIO_TOOLKITS.join(", ")}.`);

export const COMPOSIO_TOOLS_METADATA = [
  {
    name: "search_tools",
    description:
      "Find the Composio app action that fulfils a task in Linear, Jira or another connected app (e.g. 'create a Linear issue'). Returns matching tool slugs with their input schemas and whether the user has connected the app. Call this before execute_tool.",
    schema: {
      query: z
        .string()
        .describe("Plain-language description of the task to perform."),
      toolkit: toolkitSchema
        .optional()
        .describe("Restrict the search to one app."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Searching app actions",
      done: "Search app actions",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "list_connected_apps",
    description: "List which apps the user has connected through Composio.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Checking connected apps",
      done: "Check connected apps",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: "connect_app",
    description:
      "Get a link the user opens to connect an app account (e.g. Linear, Jira) through Composio. Share the link with the user and wait for them to confirm before retrying.",
    schema: {
      toolkit: toolkitSchema,
    },
    stake: "never_ask",
    displayLabels: {
      running: "Preparing app connection link",
      done: "Prepare app connection link",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: "execute_tool",
    description:
      "Run a Composio app action by its tool slug (e.g. LINEAR_CREATE_LINEAR_ISSUE, SLACK_SEND_MESSAGE) with arguments matching the input schema returned by search_tools. Files the action returns (e.g. GMAIL_GET_ATTACHMENT) are read for you: document text (PDF, Word, spreadsheets, slides, scanned images) comes back in the result and the file is attached to the conversation.",
    schema: {
      tool_slug: z
        .string()
        .describe("Exact tool slug returned by search_tools."),
      arguments: z
        .record(z.string(), z.unknown())
        .describe("Arguments object matching the tool's input schema."),
      save_to_library: z
        .boolean()
        .optional()
        .describe(
          "Also save any file the action returns (e.g. an email attachment or a Drive download) to the user's Library, so it can be found and reused from other conversations. Only set when the user asks to keep or save the file."
        ),
    },
    stake: "medium",
    displayLabels: {
      running: "Running app action",
      done: "Run app action",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
] as const;

export const COMPOSIO_SERVER = {
  serverInfo: {
    name: COMPOSIO_SERVER_NAME,
    version: "1.0.0",
    description:
      "Act in Slack, Linear, Jira and Google Workspace apps (Gmail, Calendar, Drive, Docs, Sheets, Slides, Meet, Tasks, Photos) as the user, through Composio-managed connections.",
    authorization: null,
    icon: "ActionCloudArrowLeftRightIcon",
    documentationUrl: "https://docs.composio.dev",
  },
  tools: COMPOSIO_TOOLS_METADATA,
} as const satisfies ServerMetadata;
